<template>
  <aside class="sidebar">
    <div class="sidebar-header">
      <div class="logo-icon"></div>
      <span class="sidebar-title">RTH Employee</span>
    </div>

    <!-- User Role Display -->
    <div class="user-role-section">
      <span class="role-label">Your Role:</span>
      <span class="role-badge" :class="userRole">{{ userRole || 'Loading...' }}</span>
    </div>

    <nav class="sidebar-nav">
      <ul>
        <!-- Admin and SuperAdmin only -->
        <li v-if="canAccess(['admin', 'superadmin'])">
          <router-link
            to="/admin"
            class="nav-link"
            :class="{ active: isActiveRoute('/admin') }"
          >
            <span class="link-icon"></span>
            <span class="link-text">Dashboard</span>
          </router-link>
        </li>
        <li v-if="canAccess(['admin', 'superadmin'])">
          <router-link
            to="/admin/jobs"
            class="nav-link"
            :class="{ active: isActiveRoute('/admin/jobs') }"
          >
            <span class="link-icon"></span>
            <span class="link-text">Jobs</span>
          </router-link>
        </li>
        <li v-if="canAccess(['admin', 'superadmin'])">
          <router-link
            to="/admin/messages"
            class="nav-link"
            :class="{ active: isActiveRoute('/admin/messages') }"
          >
            <span class="link-icon"></span>
            <span class="link-text">Messages</span>
          </router-link>
        </li>
        <!-- Admin and SuperAdmin only -->
        <li v-if="canAccess(['admin', 'superadmin'])">
          <router-link
            to="/admin/adduser"
            class="nav-link"
            :class="{ active: isActiveRoute('/admin/adduser') }"
          >
            <span class="link-icon"></span>
            <span class="link-text">Add User</span>
          </router-link>
        </li>
        <li v-if="canAccess(['admin', 'superadmin'])">
          <router-link
            to="/admin/listusers"
            class="nav-link"
            :class="{ active: isActiveRoute('/admin/listusers') }"
          >
            <span class="link-icon"></span>
            <span class="link-text">Users</span>
          </router-link>
        </li>
        <li v-if="canAccess(['admin', 'superadmin'])">
          <router-link
            to="/admin/updateemployee"
            class="nav-link"
            :class="{ active: isActiveRoute('/admin/updateemployee') }"
          >
            <span class="link-icon"></span>
            <span class="link-text">Update Employee</span>
          </router-link>
        </li>

        <!-- Payroll and SuperAdmin only -->
        <li v-if="canAccess(['payroll', 'superadmin'])">
          <router-link
            to="/admin/addproperty"
            class="nav-link"
            :class="{ active: isActiveRoute('/admin/addproperty') }"
          >
            <span class="link-icon"></span>
            <span class="link-text">Add Property</span>
          </router-link>
        </li>
        <li v-if="canAccess(['payroll', 'superadmin'])">
          <router-link
            to="/admin/createinvoice"
            class="nav-link"
            :class="{ active: isActiveRoute('/admin/createinvoice') }"
          >
            <span class="link-icon"></span>
            <span class="link-text">Create Invoice</span>
          </router-link>
        </li>
        <li v-if="canAccess(['payroll', 'superadmin'])">
          <router-link
            to="/admin/createpaystub"
            class="nav-link"
            :class="{ active: isActiveRoute('/admin/createpaystub') }"
          >
            <span class="link-icon"></span>
            <span class="link-text">Create Pay Stub</span>
          </router-link>
        </li>
      </ul>
    </nav>

    <button class="logout-button" @click="handleLogout">Logout</button>
  </aside>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { auth, db } from '@/firebase'
import { signOut, onAuthStateChanged } from 'firebase/auth'               
import { collection, query, where, getDocs, updateDoc } from 'firebase/firestore'

const router = useRouter()
const route = useRoute()
const userRole = ref(null)

// Fetch current user's role on mount
onMounted(async () => {
  onAuthStateChanged(auth, async (authUser) => {
    if (authUser) {
      try {
        const q = query(collection(db, 'users'), where('uid', '==', authUser.uid))
        const querySnapshot = await getDocs(q)
        if (!querySnapshot.empty) {
          userRole.value = querySnapshot.docs[0].data().role
        } else {
          // Fallback: try searching by email
          const emailQuery = query(collection(db, 'users'), where('email', '==', authUser.email))
          const emailSnapshot = await getDocs(emailQuery)
          if (!emailSnapshot.empty) {
            userRole.value = emailSnapshot.docs[0].data().role
          }
        }
      } catch (err) {
        console.error('Error fetching user role:', err)
      }
    }
  })
})

// Helper function to check if user can access a feature
function canAccess(requiredRoles) {
  if (!userRole.value) return false
  return requiredRoles.includes(userRole.value)
}

function isActiveRoute(pathPrefix) {
  return route.path.startsWith(pathPrefix)
}

async function handleLogout() {
  const user = auth.currentUser
  if (user) {
    try {
      const usersRef = collection(db, 'users')
      const q = query(usersRef, where('uid', '==', user.uid))
      const snap = await getDocs(q)

      if (!snap.empty) {
        const userDocRef = snap.docs[0].ref
        await updateDoc(userDocRef, {
          status: 'logged out',
          lastLogout: new Date(),
        })
      }
    } catch (err) {
      console.error('Error updating Firestore on logout:', err)
    }

    await signOut(auth)
  }

  router.replace({ name: 'Login' })
}
</script>

<style scoped>
/* ========== Sidebar Container ========== */
.sidebar {
  width: 240px;
  background-color: #1e3a8a;
  color: #ffffff;
  display: flex;
  flex-direction: column;
  padding: 16px;
  /* remove min-height: 100vh so that it only grows as tall as needed */
}

/* ========== Sidebar Header ========== */
.sidebar-header {
  display: flex;
  align-items: center;
  margin-bottom: 32px;
}
.logo-icon {
  width: 32px;
  height: 32px;
  background-color: #60a5fa;
  border-radius: 6px;
  margin-right: 12px;
}
.sidebar-title {
  font-size: 20px;
  font-weight: bold;
}

/* ========== User Role Section ========== */
.user-role-section {
  background-color: rgba(96, 165, 250, 0.1);
  border: 1px solid rgba(96, 165, 250, 0.3);
  border-radius: 6px;
  padding: 12px;
  margin-bottom: 24px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.role-label {
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #94a3b8;
  font-weight: 600;
}

.role-badge {
  display: inline-block;
  padding: 6px 10px;
  border-radius: 4px;
  font-size: 13px;
  font-weight: 600;
  text-transform: capitalize;
  text-align: center;
}

.role-badge.admin {
  background-color: #3b82f6;
  color: #ffffff;
}

.role-badge.payroll {
  background-color: #10b981;
  color: #ffffff;
}

.role-badge.superadmin {
  background-color: #f59e0b;
  color: #ffffff;
}

/* ========== Nav Links ========== */
.sidebar-nav ul {
  list-style: none;
  padding: 0;
  margin: 0;
}
.nav-link {
  display: flex;
  align-items: center;
  text-decoration: none;
  color: #cbd5e1;
  padding: 8px 12px;
  border-radius: 4px;
  margin-bottom: 12px;
  transition: background-color 0.2s ease;
}
.nav-link:hover {
  background-color: #273c8a;
  color: #ffffff;
}
/* Active state for whichever link matches the route */
.nav-link.active {
  background-color: #3b5bcd;
  color: #ffffff;
}
.link-icon {
  width: 16px;
  height: 16px;
  background-color: #cbd5e1;
  border-radius: 3px;
  margin-right: 8px;
}
/* Make the icon brighter when its link is active */
.nav-link.active .link-icon {
  background-color: #bfdbfe;
}

/* ========== Logout Button ========== */
.logout-button {
  margin-top: auto;           /* push it to the bottom of the sidebar */
  padding: 8px 12px;
  background-color: #ef4444;
  color: #ffffff;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 600;
  transition: background-color 0.2s ease;
}
.logout-button:hover {
  background-color: #dc2626;
}

/* ========== Responsive (hide sidebar on small screens) ========== */
@media (max-width: 768px) {
  .sidebar {
    display: none;
  }
}
</style>
